import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  IRichViewLogsProps,
  productionLogStateAtom,
  RydeUiUnit,
  SINGLE_KEY,
  TDrawerCloseFunction,
} from "@ryde/shared";

import {
  resolveRichLogsDataForExport,
  useRichLogsExportCsvData,
} from "./hooks/use-rich-logs.export-csv-data";
import { useRichLogsFilteredRecords } from "./hooks/use-rich-logs.filtered-records";
import { RichLogsHeaderUi } from "./rich-logs.header.ui";

interface IRichLogsHeader extends IRichViewLogsProps {
  entity: RydeUiUnit;
  isFinished?: boolean;
  onCloseDrawer: TDrawerCloseFunction;
  title?: string;
  idOnlyLinks?: boolean;
}

export const RichLogsHeader: FC<IRichLogsHeader> = ({
  entity,
  isFinished,
  logsFormWatched,
  logsFromWatchedApiOld,
  onCloseDrawer,
  title: titleOverride,
  idOnlyLinks,
}) => {
  const { t } = useTranslation();
  const logData = useRecoilValue(productionLogStateAtom);

  const logDataForExport = useMemo(
    () =>
      resolveRichLogsDataForExport({
        logsFormWatched,
        isFinished,
        logDataFromAtom: logData,
      }),
    [isFinished, logData, logsFormWatched],
  );

  const filteredRecords = useRichLogsFilteredRecords({
    logData: logDataForExport,
  });

  const hasRichRecords = Array.isArray(logDataForExport.records);

  const allLogsCsvData = useRichLogsExportCsvData({
    records: logDataForExport.records,
    logsFromWatchedApiOld: hasRichRecords ? undefined : logsFromWatchedApiOld,
    idOnlyLinks,
  });

  const filteredLogsCsvData = useRichLogsExportCsvData({
    records: hasRichRecords ? filteredRecords : undefined,
    logsFromWatchedApiOld: hasRichRecords ? undefined : logsFromWatchedApiOld,
    idOnlyLinks,
  });

  const title = titleOverride ?? t(`${entity}.logsPreview`);
  const fileName = titleOverride
    ? `${titleOverride}-${Number(new Date())}.csv`
    : `${t(`${entity}.${SINGLE_KEY}`)}-logs.${Number(new Date())}.csv`;

  return (
    <RichLogsHeaderUi
      allLogsCsvData={allLogsCsvData}
      filteredLogsCsvData={filteredLogsCsvData}
      title={title}
      fileName={fileName}
      onCloseDrawer={onCloseDrawer}
    />
  );
};
